import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from "react";
import {
  allPacksStats, listRuns, packStats, STATS_RANGES,
  type BobRun, type BobStatsResponse, type PackPosture, type StatsRange,
} from "../../api/bobReports";
import {
  axisTicks, fmtDay, fmtWhen, pct, repoName, runActivityText, shortSha, sparkPoints, trendGeometry, usd, usdShort,
} from "../../lib/bobReports";
import type { ReportNav } from "./reportNav";
import { Failed, Loading, SevChip, SevStack } from "./reportParts";
import "./bobTheme.css";
import "./bobReports.css";

type Scope = "all" | "pack";
type FeedRun = BobRun & { project: string; packName: string };

const FEED_SIZE = 8;
const SPEND_WEEKS = 8;

function sum(o: Record<string, number> | undefined): number {
  return Object.values(o ?? {}).reduce((a, b) => a + (typeof b === "number" ? b : 0), 0);
}

const packLabel = (p: PackPosture) => p.project_name ?? repoName(p.repo);

/** Worst first: most open BLOCK, then most open. */
function worstPacksFirst(packs: readonly PackPosture[]): PackPosture[] {
  return [...packs].sort((a, b) => (b.open?.BLOCK ?? 0) - (a.open?.BLOCK ?? 0) || (b.open_total ?? 0) - (a.open_total ?? 0));
}

/** Categories shown before the rest fold behind a "more" button. */
const CATEGORY_FOLD = 8;

function packsOf(stats: BobStatsResponse, nav: ReportNav): PackPosture[] {
  if (stats.per_repo) return stats.per_repo;
  return [{ ...stats, repo: stats.repo, branch: stats.branch ?? "", project: nav.project, project_name: nav.projectName }];
}

function fixedNote(packs: PackPosture[]): string {
  const fixed = packs.filter((p) => p.fixed_last_7d > 0).sort((a, b) => b.fixed_last_7d - a.fixed_last_7d);
  if (fixed.length === 0) return "No finding was confirmed fixed this week.";
  if (packs.length === 1) return `On ${packLabel(packs[0]!)}.`;
  const named = fixed.map((p) => `${p.fixed_last_7d} ${packLabel(p)}`).join(", ");
  const rest = packs.length - fixed.length;
  if (rest === 0) return `${named}.`;
  return `${named}. Nothing on the other ${rest === 1 ? "pack" : `${rest} packs`}.`;
}

/** Links into this pack switch tabs in place; another pack's are ordinary links to its page. */
function PackLink({ project, nav, className, children, onHere }: {
  project: string | undefined;
  nav: ReportNav;
  className?: string;
  children: ReactNode;
  onHere: () => void;
}) {
  if (!project) return <div className={className}>{children}</div>;
  const here = project === nav.project;
  return (
    <a
      className={className}
      href={nav.packHref(project)}
      onClick={(e: MouseEvent) => {
        if (!here || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        onHere();
      }}
    >
      {children}
    </a>
  );
}

function PackCard({ pack, nav }: { pack: PackPosture; nav: ReportNav }) {
  const label = packLabel(pack);
  const series = pack.weekly.map((w) => w.open_at_end);
  const last = pack.last_completed_run;
  const showSpend = nav.isAdmin && typeof pack.spend_total_usd === "number";
  return (
    <PackLink project={pack.project} nav={nav} className="br-packcard" onHere={() => nav.openTab("ledger")}>
      <div className="br-packcard-top">
        <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          <span style={{ fontWeight: 600, fontSize: 15 }}>{label}</span>
          <span className="br-mono br-muted" style={{ fontSize: 11.5, overflowWrap: "anywhere" }}>{repoName(pack.repo)} / {pack.branch}</span>
        </div>
        {(pack.open.BLOCK ?? 0) > 0 && <SevChip severity="BLOCK" count={pack.open.BLOCK} size="sm" />}
      </div>
      <div className="br-packcard-mid">
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span className="br-big" style={{ fontSize: 28 }}>{pack.open_total}</span>
          <span className="br-tiny br-muted">open</span>
        </div>
        {series.length > 1 && (
          <svg viewBox="0 0 120 40" width="120" height="40" role="img" style={{ overflow: "visible" }}
            aria-label={`${label} open findings per week: ${series.join(", ")}`}>
            <polyline points={sparkPoints(series)} fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
          </svg>
        )}
      </div>
      <SevStack counts={pack.open} total={pack.open_total} />
      <div className="br-packcard-stats" style={{ gridTemplateColumns: `repeat(${showSpend ? 3 : 2}, minmax(0, 1fr))` }}>
        <div><span className="br-muted">Fixed 7 d</span><span className="br-num">{pack.fixed_last_7d}</span></div>
        <div><span className="br-muted">Fix rate</span><span className="br-num">{pct(pack.fix_rate)}</span></div>
        {showSpend && <div><span className="br-muted">Spend</span><span className="br-num">{usdShort(pack.spend_total_usd!)}</span></div>}
      </div>
      <span className="br-tiny br-muted">
        {last ? `Last review ${fmtDay(last.created_at)}, ${shortSha(last.head_sha)}` : "No completed review yet"}
      </span>
    </PackLink>
  );
}

function TrendChart({ stats, range, onRange, scopeLabel }: {
  stats: BobStatsResponse;
  range: StatsRange;
  onRange: (r: StatsRange) => void;
  scopeLabel: string;
}) {
  const weeks = stats.weekly;
  const values = weeks.map((w) => w.open_at_end);
  const ticks = axisTicks(Math.max(...values, stats.open_total, 1));
  const { line, area } = trendGeometry(values, ticks[0]!);
  const [hover, setHover] = useState<number | null>(null);
  const plot = useRef<HTMLDivElement>(null);
  const x = (i: number) => (values.length === 1 ? 50 : (100 * i) / (values.length - 1));
  const y = (v: number) => 100 - (100 * v) / (ticks[0] || 1);

  const onMove = (e: MouseEvent) => {
    const box = plot.current?.getBoundingClientRect();
    if (!box || box.width === 0 || values.length === 0) return;
    const f = (e.clientX - box.left) / box.width;
    setHover(Math.max(0, Math.min(values.length - 1, Math.round(f * (values.length - 1)))));
  };
  const mid = weeks[Math.floor((weeks.length - 1) / 2)];
  const h = hover === null ? null : weeks[hover];

  return (
    <section className="br-panel" aria-label="Open findings over time">
      <div className="br-row">
        <div style={{ display: "flex", flexDirection: "column", flex: "1 1 280px" }}>
          <h2 className="br-h2">Open findings over time</h2>
          <span className="br-small br-muted">
            Last {range} days, {scopeLabel}, counted at the end of each week. Weeks start on Monday.
          </span>
        </div>
        <div className="br-seg" role="group" aria-label="Time range">
          {STATS_RANGES.map((r) => (
            <button key={r} type="button" aria-pressed={range === r} onClick={() => onRange(r)}>{r} days</button>
          ))}
        </div>
      </div>
      {values.length === 0 ? (
        <p className="br-p">No weeks in this window yet.</p>
      ) : (
        <div className="br-chart">
          <div className="br-chart-y br-mono" aria-hidden="true">{ticks.map((t) => <span key={t}>{t}</span>)}</div>
          <div className="br-chart-plot" ref={plot} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
            <svg viewBox="0 0 1000 240" preserveAspectRatio="none" width="100%" height="240" role="img"
              aria-label={`Open findings, ${scopeLabel}, ${values.length} weeks, from ${values[0]} to ${values[values.length - 1]}`}>
              {[0.5, 80, 160].map((gy) => (
                <line key={gy} x1="0" y1={gy} x2="1000" y2={gy} stroke="var(--line-soft)" vectorEffect="non-scaling-stroke" />
              ))}
              <line x1="0" y1="239.5" x2="1000" y2="239.5" stroke="var(--line)" vectorEffect="non-scaling-stroke" />
              <polygon points={area} fill="var(--accent-soft)" />
              <polyline points={line} fill="none" stroke="var(--accent)" strokeWidth="2.5" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
              {hover !== null && (
                <line x1={x(hover) * 10} y1="0" x2={x(hover) * 10} y2="240" stroke="var(--ink-3)" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
              )}
            </svg>
            {h && hover !== null && (
              <>
                <span aria-hidden="true" style={{
                  position: "absolute", left: `${x(hover)}%`, top: `${y(h.open_at_end) * 2.4}px`, width: 9, height: 9,
                  marginLeft: -4.5, marginTop: -4.5, borderRadius: "50%", background: "var(--accent)", boxShadow: "0 0 0 2px var(--panel)",
                }} />
                <span className="br-chart-tip" style={{ left: `${x(hover)}%`, top: `${y(h.open_at_end) * 2.4}px` }}>
                  Week of {fmtDay(h.week_start)}: <b className="br-num">{h.open_at_end}</b> open, {h.opened} opened, {h.fixed} fixed
                </span>
              </>
            )}
            <div className="br-chart-badge">
              <span className="br-num" style={{ fontWeight: 600 }}>{stats.open_total}</span>
              <span className="br-muted">open on {fmtDay(stats.generated_at)}</span>
            </div>
          </div>
          <span />
          <div className="br-chart-x br-mono">
            <span>{fmtDay(weeks[0]!.week_start)}</span>
            {weeks.length > 2 && mid && <span>{fmtDay(mid.week_start)}</span>}
            <span>{fmtDay(weeks[weeks.length - 1]!.week_start)}</span>
          </div>
        </div>
      )}
      <table className="br-sr">
        <caption>Open findings at the end of each week</caption>
        <thead><tr><th>Week of</th><th>Open</th><th>Opened</th><th>Fixed</th></tr></thead>
        <tbody>
          {weeks.map((w) => (
            <tr key={w.week_start}><td>{w.week_start}</td><td>{w.open_at_end}</td><td>{w.opened}</td><td>{w.fixed}</td></tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function BarRows({ rows, color, spend }: {
  rows: Array<{ key: string; label: ReactNode; value: number; text: ReactNode }>;
  color: (key: string) => string;
  spend?: boolean;
}) {
  const max = Math.max(...rows.map((r) => r.value), 1);
  return (
    <div className="br-bars">
      {rows.map((r) => (
        <div key={r.key} className={`br-bar-row${spend ? " br-spend" : ""}`}>
          <span style={{ minWidth: 0 }}>{r.label}</span>
          <div className="br-track"><div className={`br-fill ${color(r.key)}`} style={{ width: `${((100 * r.value) / max).toFixed(1)}%` }} /></div>
          <span className="br-num" style={{ fontSize: 13 }}>{r.text}</span>
        </div>
      ))}
    </div>
  );
}

export default function BobOverview({ nav }: { nav: ReportNav }) {
  const [scope, setScope] = useState<Scope>("all");
  const [range, setRange] = useState<StatsRange>(30);
  const [stats, setStats] = useState<BobStatsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [feed, setFeed] = useState<FeedRun[] | null>(null);
  const [feedFailed, setFeedFailed] = useState(false);
  const [latestPush, setLatestPush] = useState<number | null>(null);
  const [retry, setRetry] = useState(0);
  const gen = useRef(0);

  useEffect(() => {
    const mine = ++gen.current;
    setError(null);
    const read = scope === "all" ? allPacksStats(range) : packStats(nav.project, range);
    read.then(
      (s) => { if (gen.current === mine) setStats(s); },
      (e: Error) => { if (gen.current === mine) { setStats(null); setError(e.message); } },
    );
  }, [scope, range, nav.project, retry]);

  const packProjects = stats
    ? packsOf(stats, nav).map((p) => ({ project: p.project, name: packLabel(p) })).filter((p): p is { project: string; name: string } => !!p.project)
    : null;
  const packKey = packProjects?.map((p) => p.project).join(",") ?? null;
  const packList = useRef(packProjects);
  packList.current = packProjects;

  useEffect(() => {
    const list = packList.current;
    if (packKey === null || !list) return;
    let live = true;
    setFeed(null);
    setFeedFailed(false);
    Promise.allSettled(list.map((p) =>
      listRuns(p.project, { limit: FEED_SIZE }).then((r) => r.items.map((run) => ({ ...run, project: p.project, packName: p.name })))))
      .then((results) => {
        if (!live) return;
        const ok = results.flatMap((r) => (r.status === "fulfilled" ? r.value : []));
        setLatestPush(ok.find((r) => r.project === nav.project && r.kind === "push")?.id ?? null);
        setFeedFailed(results.length > 0 && results.every((r) => r.status === "rejected"));
        setFeed(ok.sort((a, b) => (a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : b.id - a.id)).slice(0, FEED_SIZE));
      });
    return () => { live = false; };
  }, [packKey]);

  const scopeLabel = scope === "all" ? "all packs" : nav.projectName;

  return (
    <div className="bob br-root">
      <div className="br-page">
        <header className="br-header">
          <div className="br-header-text">
            <span className="br-eyebrow">Bob code review</span>
            <h2 className="br-h1">{scope === "all" ? "Posture across all packs" : `Posture for ${nav.projectName}`}</h2>
            {stats && (
              <span className="br-soft">
                Ledger from {fmtDay(stats.window_start)} to {fmtDay(stats.generated_at)}. {stats.open_total} open findings,{" "}
                {sum(stats.runs_by_kind)} reviews, {packsOf(stats, nav).length} {packsOf(stats, nav).length === 1 ? "pack" : "packs"}.
              </span>
            )}
          </div>
          <nav aria-label="Console sections" className="br-nav">
            <div className="br-seg" role="group" aria-label="Which packs">
              <button type="button" aria-pressed={scope === "all"} onClick={() => setScope("all")}>All packs</button>
              <button type="button" aria-pressed={scope === "pack"} onClick={() => setScope("pack")}>{nav.projectName}</button>
            </div>
            <button type="button" className="br-btn" onClick={() => nav.openTab("ledger")}>Open console</button>
            {latestPush !== null && <button type="button" className="br-btn" onClick={() => nav.openRun(latestPush)}>Latest push report</button>}
          </nav>
        </header>

        {error ? <Failed message={`Bob's posture could not be loaded: ${error}`} onRetry={() => setRetry((n) => n + 1)} />
          : !stats ? <Loading what="Bob's posture" />
          : <OverviewBody stats={stats} nav={nav} range={range} onRange={setRange} scopeLabel={scopeLabel} feed={feed} feedFailed={feedFailed} />}
      </div>
    </div>
  );
}

function OverviewBody({ stats, nav, range, onRange, scopeLabel, feed, feedFailed }: {
  stats: BobStatsResponse;
  nav: ReportNav;
  range: StatsRange;
  onRange: (r: StatsRange) => void;
  scopeLabel: string;
  feed: FeedRun[] | null;
  feedFailed: boolean;
}) {
  const packs = packsOf(stats, nav);
  const byRepo = new Map(packs.map((p) => [p.repo, p]));
  const [allCategories, setAllCategories] = useState(false);
  const reviews = sum(stats.runs_by_kind);
  const admin = nav.isAdmin && typeof stats.spend_total_usd === "number";
  const spendWeeks = stats.weekly.slice(-SPEND_WEEKS);
  const maxWeek = Math.max(...spendWeeks.map((w) => w.spend_usd ?? 0), 0);

  return (
    <>
      <section aria-label="Headline numbers" className="br-grid-tiles">
        <div className="br-panel br-tight">
          <span className="br-eyebrow">Open findings</span>
          <span className="br-big">{stats.open_total}</span>
          <SevStack counts={stats.open} total={stats.open_total} />
          <span className="br-small br-soft">
            <span style={{ color: "var(--block)", fontWeight: 600 }}>{stats.open.BLOCK ?? 0} BLOCK</span>, {stats.open.FLAG ?? 0} FLAG, {stats.open.SUGGEST ?? 0} SUGGEST
          </span>
        </div>
        <div className="br-panel br-tight">
          <span className="br-eyebrow">Fixed, last 7 days</span>
          <span className="br-big br-ok">{stats.fixed_last_7d}</span>
          <span className="br-small br-soft">{fixedNote(packs)}</span>
        </div>
        <div className="br-panel br-tight">
          <span className="br-eyebrow">Fix rate, last {range} days</span>
          <span className="br-big">{pct(stats.fix_rate)}</span>
          <span className="br-small br-soft">{stats.fixed_in_window} fixed in the window, {stats.open_total} still open.</span>
        </div>
        {admin && (
          <div className="br-panel br-tight">
            <span className="br-eyebrow">Spend, last {range} days</span>
            <span className="br-big">{usd(stats.spend_total_usd!)}</span>
            <span className="br-small br-soft">
              {reviews} {reviews === 1 ? "review" : "reviews"}{reviews > 0 ? `, ${usd(stats.spend_total_usd! / reviews)} average` : ""}.
            </span>
          </div>
        )}
      </section>

      <section aria-label="Packs" className="br-grid-packs">
        {worstPacksFirst(packs).map((p) => <PackCard key={p.repo} pack={p} nav={nav} />)}
      </section>

      <TrendChart stats={stats} range={range} onRange={onRange} scopeLabel={scopeLabel} />

      <div className="br-grid-two br-grid-pairs">
        <section className="br-panel" aria-label="Top categories">
          <h2 className="br-h2">Top categories, open</h2>
          {stats.open_by_category.length === 0 ? <p className="br-p">No open findings.</p> : (
            <BarRows
              color={() => "br-bg-ink"}
              rows={(allCategories ? stats.open_by_category : stats.open_by_category.slice(0, CATEGORY_FOLD)).map((c) => ({
                key: c.category,
                label: <span className="br-mono br-soft" style={{ fontSize: 12.5 }}>{c.category}</span>,
                value: c.open,
                text: c.open,
              }))}
            />
          )}
          {stats.open_by_category.length > CATEGORY_FOLD && (
            <button type="button" className="br-linkbtn" style={{ alignSelf: "flex-start" }} aria-expanded={allCategories} onClick={() => setAllCategories(!allCategories)}>
              {allCategories ? "Fewer" : `${stats.open_by_category.length - CATEGORY_FOLD} more`}
            </button>
          )}
        </section>

        <section className="br-panel" aria-label="Hot files">
          <h2 className="br-h2">Hot files, open findings per file</h2>
          {stats.hottest_files.length === 0 ? <p className="br-p">No open findings.</p> : (
            <div className="br-filelist">
              {stats.hottest_files.map((f) => {
                const pack = byRepo.get(f.repo);
                return (
                  <PackLink key={`${f.repo}:${f.file}`} project={pack?.project} nav={nav} className="br-filerow" onHere={() => nav.openTab("ledger")}>
                    <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                      <span className="br-path" title={f.file}>{f.file}</span>
                      {packs.length > 1 && <span className="br-muted" style={{ fontSize: 11.5 }}>{pack ? packLabel(pack) : repoName(f.repo)}</span>}
                    </span>
                    <span className="br-num" style={{ textAlign: "right" }}>{f.open}</span>
                  </PackLink>
                );
              })}
            </div>
          )}
        </section>

        {admin && packs.length > 1 && (
          <section className="br-panel" aria-label="Spend per pack">
            <h2 className="br-h2">Spend per pack</h2>
            <BarRows
              spend
              color={() => "br-bg-accent"}
              rows={[...packs].sort((a, b) => (b.spend_total_usd ?? 0) - (a.spend_total_usd ?? 0)).map((p) => {
                const runs = sum(p.runs_by_kind);
                return {
                  key: p.repo,
                  label: <span style={{ fontSize: 13 }}>{packLabel(p)}</span>,
                  value: p.spend_total_usd ?? 0,
                  text: <>{usd(p.spend_total_usd ?? 0)} <span className="br-muted">{runs} {runs === 1 ? "run" : "runs"}</span></>,
                };
              })}
            />
          </section>
        )}

        {admin && spendWeeks.length > 0 && (
          <section className="br-panel" aria-label="Spend per week">
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
              <h2 className="br-h2">Spend per week</h2>
              <span className="br-small br-muted">Week starting Monday</span>
            </div>
            <div className="br-weeks">
              {spendWeeks.map((w) => (
                <div key={w.week_start} className="br-week" title={`Week of ${fmtDay(w.week_start)}: ${usd(w.spend_usd ?? 0)}, ${w.runs} runs`}>
                  <span className="br-num br-soft" style={{ fontSize: 11.5 }}>{usdShort(w.spend_usd ?? 0)}</span>
                  <div className="br-week-bar" style={{ height: `${maxWeek > 0 ? Math.max(2, Math.round((140 * (w.spend_usd ?? 0)) / maxWeek)) : 2}px` }} />
                  <span className="br-mono br-muted" style={{ fontSize: 11 }}>{fmtDay(w.week_start)}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      <section className="br-panel" aria-label="Recent activity">
        <h2 className="br-h2">Recent activity</h2>
        {feed === null ? <Loading what="recent reviews" />
          : feedFailed ? <p className="br-p">Recent reviews could not be loaded.</p>
          : feed.length === 0 ? <p className="br-p">Bob has not reviewed anything yet.</p>
          : (
            <ol className="br-feed">
              {feed.map((r) => (
                <li key={`${r.project}:${r.id}`}>
                  <span className="br-mono br-muted br-tiny">{fmtWhen(r.created_at)}</span>
                  <span className="br-feed-pack" title={r.packName}>{r.packName}</span>
                  <a
                    className="br-feed-text"
                    title={runActivityText(r)}
                    href={r.project === nav.project ? nav.runHref(r.id) : `${nav.packHref(r.project)}?run=${r.id}`}
                    onClick={(e) => {
                      if (r.project !== nav.project || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                      e.preventDefault();
                      nav.openRun(r.id);
                    }}
                  >
                    {runActivityText(r)}
                  </a>
                  <span className="br-num br-muted" style={{ fontSize: 12.5 }}>
                    {nav.isAdmin && typeof r.cost_usd === "number" ? usd(r.cost_usd) : ""}
                  </span>
                </li>
              ))}
            </ol>
          )}
      </section>
    </>
  );
}
